import { Text, Group, Loader } from '@mantine/core';
import { FileText } from 'react-feather';
import { useEffect, useRef, useState } from 'react';
import { ContentCard } from '../../../../components/common/ContentCard';
import {
  RichTextEditor,
  LINK_CLICKABILITY_HELPER_TEXT,
} from '../../../../components/common/RichTextEditor';
import { coachActiveSessionApi } from '../../../../services/api/coachActiveSession';
import { notifications } from '@mantine/notifications';

/**
 * MED-792 AC9 — right column "Session Notes" panel.
 *
 * Coach-only WYSIWYG for real-time observations. Uses the existing TipTap
 * RichTextEditor. Saves to PATCH /coach-notes with a 600ms debounce so
 * typing latency stays subjective-instant while server writes are batched.
 *
 * Notes are personal to the coach (per AC9) — never rendered to participants.
 */

export interface ActiveSessionNotesPanelProps {
  sessionId: string;
  initialHtml: string;
  initialUpdatedAt: string | null;
  /**
   * MED-873 — when true, render the panel in read-only preview mode:
   *   - TipTap editor is disabled (no edits accepted).
   *   - Debounced save is skipped (no PATCH /coach-notes call).
   *   - "Saving…" / "Saved at" indicator is hidden.
   */
  isReadOnly?: boolean;
  /**
   * MED-872 — fires on every content change (pre-debounce) so the page can keep
   * a canonical copy of the notes HTML. Lets the AI "Save to notes" action append
   * to the freshest content without clobbering the coach's in-progress edits.
   */
  onContentChange?: (html: string) => void;
}

const SAVE_DEBOUNCE_MS = 600;

export function ActiveSessionNotesPanel({
  sessionId,
  initialHtml,
  isReadOnly = false,
  onContentChange,
}: ActiveSessionNotesPanelProps) {
  const [value, setValue] = useState(initialHtml);
  const [isSaving, setIsSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string>(initialHtml);

  // Cancel any pending save on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const persist = (html: string) => {
    setIsSaving(true);
    coachActiveSessionApi
      .updateCoachNotes(sessionId, { content_html: html })
      .then(() => {
        lastSentRef.current = html;
      })
      .catch(() => {
        notifications.show({
          color: 'red',
          title: "Couldn't save notes",
          message: 'Your last change did not save. Please try again.',
        });
      })
      .finally(() => setIsSaving(false));
  };

  const handleChange = (html: string) => {
    if (isReadOnly) return;
    setValue(html);
    onContentChange?.(html);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (html === lastSentRef.current) return;
    debounceRef.current = setTimeout(() => persist(html), SAVE_DEBOUNCE_MS);
  };

  return (
    <ContentCard
      data-testid="active-session-notes-panel"
      flushContent
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap={6} align="center" wrap="nowrap">
            <FileText
              size={16}
              color="#6B7280"
              aria-hidden="true"
              style={{ position: 'relative', top: -2 }}
            />
            <Text fz={12} fw={600} c="#374151">
              Session Notes
            </Text>
            <Text
              fz={10}
              c="dimmed"
              tt="uppercase"
              style={{ letterSpacing: '0.4px' }}
            >
              Only you
            </Text>
          </Group>
          {!isReadOnly && (
            <Group gap={6} align="center" wrap="nowrap">
              {isSaving && <Loader size="xs" />}
              <Text
                fz={11}
                c="dimmed"
                data-testid="active-session-notes-saved-at"
              >
                {isSaving ? 'Saving…' : 'Saved'}
              </Text>
            </Group>
          )}
        </Group>
      }
    >
      <RichTextEditor
        value={value}
        onChange={handleChange}
        placeholder="Capture observations, follow-ups, and reminders for yourself…"
        disabled={isReadOnly}
        fillHeight
        seamless
        toolbar={[
          ['bold', 'italic', 'underline'],
          ['bulletList', 'orderedList'],
          ['link'],
        ]}
        helperText={LINK_CLICKABILITY_HELPER_TEXT}
      />
    </ContentCard>
  );
}
